import { useState, useEffect, useRef } from 'react';

import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { Tooltip } from '@components/tooltip';
import { abbreviateDate } from '@utils/date';

import { applicantStatusTranslationMap } from 'locale';

import { ApplicationMetaRow } from 'components/application-meta-row';
import { ApplicationLabels } from 'components/ApplicationLabels';
import { Box } from 'components/Box';
import { Loading } from 'components/Loading';
import { getApplicationStatus, getStatusDetail } from 'helpers/application-status';
import { useStore } from 'models/Provider';

import { OnboardingApplicationDetails, VerifySsn } from './styles';

function ApplicationOnboardingDetails({ application, useSsnRegistry }) {
  const { t } = useStore();
  const [updating, setUpdating] = useState(false);
  const [editingSsn, setEditingSsn] = useState(false);
  const [ssn, setSsn] = useState(application.ssn);
  const ssnRef = useRef();

  useEffect(() => {
    application.setUserRead('taskList');
  }, [application]);

  useEffect(() => {
    setSsn(application.ssn);
  }, [application.ssn]);

  function startEditing() {
    setEditingSsn(true);
    ssnRef.current.focus();
  }

  function reset() {
    setSsn(application.ssn);
    setEditingSsn(false);
  }

  async function saveEditedSsn() {
    setUpdating(true);
    const success = await application.updateSsn(ssn);
    if (success) {
      reset();
    }
    setUpdating(false);
  }

  function updateSsn(value) {
    const val = value.trim();
    if (!isNaN(val)) {
      setSsn(val);
    }
  }

  async function wrapUpdate(fn) {
    setUpdating(true);
    await fn();
    setUpdating(false);
  }

  function verify() {
    wrapUpdate(application.verifySsn);
  }

  function matchName() {
    wrapUpdate(application.matchName);
  }

  function keepName() {
    wrapUpdate(application.keepName);
  }

  function renderNameIcon() {
    if (!useSsnRegistry) return null;

    switch (application.ssnStatus) {
      case 'unverified':
        return <Exclamation />;
      case 'verified':
        return <Checkmark />;
      case 'ssn_only':
        return (
          <Checkmark
            neutral
            tip={t('application.onboarding.tip.ssn_only', application.ssnRegistry.name)}
          />
        );
      case 'keep_or_match':
        return (
          <Mismatch
            tip={t('application.onboarding.tip.keep_or_match', application.ssnRegistry.name)}
          />
        );
      default:
        return null;
    }
  }

  function renderSsnIcon() {
    if (!useSsnRegistry) return null;

    switch (application.ssnStatus) {
      case 'unverified':
        return <Exclamation />;
      case 'verified':
      case 'ssn_only':
        return <Checkmark />;
      case 'error':
        return <Mismatch tip={t('application.onboarding.tip.error')} />;
      default:
        return null;
    }
  }

  const { lang } = useStore();
  const stillSameSsn = application.ssn === ssn;
  const dt = abbreviateDate(application.date, lang);
  const status = getApplicationStatus(application);
  const statusDetail = getStatusDetail({ application, status, lang, t });

  return (
    <>
      <Box flex>
        <Avatar src={application.picture} name={application.name} size="xl" />
        <Box mt="8" ml="16">
          <Stack space={2}>
            <Stack space={1}>
              <Text font="heading2" text={application.name} />
              <ApplicationMetaRow
                variant="neutral"
                label={t('application.onboarding.applied')}
                detail={dt}
              />
              <ApplicationMetaRow
                variant={status}
                label={t(applicantStatusTranslationMap[status])}
                detail={statusDetail}
              />
            </Stack>
            <ApplicationLabels application={application} />
          </Stack>
        </Box>
      </Box>
      <OnboardingApplicationDetails>
        <Stack space={4}>
          <TextField
            label={t('application.onboarding.label.name')}
            value={application.name}
            isDisabled
            iconRight={renderNameIcon()}
          />
          <TextField
            label={t('application.onboarding.label.email')}
            value={application.email}
            isDisabled
          />

          {application.phone && (
            <TextField
              label={t('application.onboarding.label.phone')}
              value={application.phone}
              isDisabled
            />
          )}

          {(application.ssn || useSsnRegistry) && (
            <TextField
              label={t('application.onboarding.label.ssn')}
              value={ssn || ''}
              ref={ssnRef}
              onChange={useSsnRegistry ? updateSsn : null}
              onFocus={useSsnRegistry ? startEditing : null}
              isDisabled={!useSsnRegistry}
              iconRight={ssn && useSsnRegistry && !editingSsn ? renderSsnIcon() : undefined}
            />
          )}

          {updating && <Loading />}

          {useSsnRegistry && application.ssnStatus === 'keep_or_match' && (
            <VerifySsn>
              <div>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'application.onboarding.verifyssn.keepormatch',
                      application.ssnRegistry.name
                    ),
                  }}
                />
              </div>
              <Box mb="8">
                <Button
                  variant="secondary"
                  style={{ width: '100%' }}
                  onClick={updating ? null : keepName}
                  isDisabled={updating}
                  text={t('application.onboarding.button.keep')}
                />
              </Box>
              <Button
                style={{ width: '100%' }}
                onClick={updating ? null : matchName}
                isDisabled={updating}
                text={t('application.onboarding.button.match')}
              />
            </VerifySsn>
          )}

          {!editingSsn &&
            application.ssn &&
            useSsnRegistry &&
            (application.ssnStatus === 'verified' ||
              application.ssnStatus === 'ssn_only' ||
              application.ssnStatus === 'error') && (
              <Button
                style={{ width: '100%' }}
                variant="secondary"
                onClick={verify}
                isDisabled={updating}
                text={t('application.onboarding.button.again')}
              />
            )}

          {editingSsn && ssn && (
            <Box flex justifyContent="space-between">
              <Button variant="secondary" onClick={reset} isDisabled={updating} text="Cancel" />
              <Button
                onClick={saveEditedSsn}
                isDisabled={stillSameSsn || updating}
                text="Update SSN"
              />
            </Box>
          )}

          {application.ssn && useSsnRegistry && application.ssnStatus === 'unverified' && (
            <VerifySsn>
              <div dangerouslySetInnerHTML={{ __html: t('application.onboarding.unverified') }} />

              <Button
                style={{ width: '100%' }}
                onClick={updating ? null : verify}
                isDisabled={updating}
                text={t('application.onboarding.button.verify')}
              />
            </VerifySsn>
          )}
        </Stack>
      </OnboardingApplicationDetails>
    </>
  );
}

export default observer(ApplicationOnboardingDetails);

function Exclamation() {
  const { t } = useStore();
  return (
    <IconFormIndicatorWarning tooltip={t('application.verifyssn.unverified')} color="orange100" />
  );
}

function Checkmark({ neutral, tip }) {
  const { t } = useStore();
  return (
    <Tooltip content={tip || t('application.verifyssn.verified')}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
          fill={!neutral ? '#05BA8C' : '#9CA6B0'}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10 17.42L5 12.42L6.41 11.01L10 14.59L17.59 7L19 8.42L10 17.42Z"
          fill="white"
        />
      </svg>
    </Tooltip>
  );
}

function Mismatch({ tip }) {
  return (
    <Tooltip content={tip}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
          fill="#EB5757"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7L17 8.41L13.41 12L17 15.59Z"
          fill="#F6F6F6"
        />
      </svg>
    </Tooltip>
  );
}
